import { create } from 'zustand';
import type { AuthUser } from '@attendance/shared';
import {
  api,
  clearPendingLogout,
  getAuthErrorContext,
  isLogoutPending,
} from '../lib/api.js';

interface AuthState {
  user: AuthUser | null;
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated';
  login: (email: string, password: string, totpToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  /**
   * Re-read the current user from /api/auth/me without rotating tokens.
   * Used after /change-password completes so the in-memory user flips
   * `mustChangePassword: false` and ProtectedRoute releases the guard.
   */
  refreshUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: 'idle',
  async login(email, password, totpToken) {
    set({ status: 'loading' });
    try {
      const result = await api.login(email, password, totpToken);
      // A deliberate sign-in retires any unconfirmed sign-out: this session is
      // new and must survive the next reload. Without this, someone who signed
      // out while the API was unreachable and then signed straight back in
      // would be dropped again on their first refresh.
      clearPendingLogout();
      set({ user: result.user, status: 'authenticated' });
    } catch (err) {
      set({ status: 'unauthenticated' });
      throw err;
    }
  },
  /**
   * Clearing local state is unconditional and happens even when the server call
   * fails. `api.logout` reports rather than throws for exactly this reason: the
   * person clicked Sign out, so the session ends here whatever the network did.
   * A server revoke that did not land is remembered and retried on next boot.
   */
  async logout() {
    try {
      await api.logout();
    } finally {
      set({ user: null, status: 'unauthenticated' });
    }
  },
  /**
   * RESTORE THE SESSION ON BOOT — AND DO NOT CONFUSE "REFUSED" WITH "UNREACHABLE".
   *
   * This was `catch { unauthenticated }`, so ANY failure of the refresh call
   * signed the reader out: a 401, and equally a request that never got an
   * answer. The second is not a statement about their session. It happens
   * whenever the API is a moment behind the page — a restart, a laptop waking,
   * a dropped connection on a site with bad signal — and the result was a
   * bounce to the sign-in screen with a perfectly valid session cookie still in
   * the jar. Someone then types a password they did not need to.
   *
   * A 401 is the server SAYING the session is over, and is obeyed immediately:
   * retrying it would be both pointless and a way to help somebody brute-force
   * a stale token.
   *
   * A transport failure is retried instead, twice, backing off. That covers the
   * blip without hanging the app: three attempts inside a second and a half,
   * and only then does it give up. Giving up still lands on the sign-in screen
   * — with the API unreachable there is nothing else the app can offer — but by
   * then it has actually tried.
   */
  async bootstrap() {
    set({ status: 'loading' });
    /* A SIGN-OUT THIS BROWSER ASKED FOR OUTRANKS A COOKIE THAT SURVIVED IT.
       If the last logout never reached the server, the httpOnly refresh cookie
       is still valid and the call below would restore the session — handing it
       to whoever opens the browser next. Retry the revoke, and either way do
       NOT resume: the person asked to be signed out. */
    if (isLogoutPending()) {
      await api.retryPendingLogout();
      set({ user: null, status: 'unauthenticated' });
      return;
    }
    // A response with a status is the server answering; only the absence of one
    // is a transport failure. 5xx is the server failing to do the job, which a
    // moment later it may manage.
    const worthRetrying = (err: unknown): boolean => {
      const status = getAuthErrorContext(err).status;
      return status === undefined || status >= 500;
    };
    for (let attempt = 0; ; attempt += 1) {
      try {
        const result = await api.refresh();
        set({ user: result.user, status: 'authenticated' });
        return;
      } catch (err) {
        if (attempt >= 2 || !worthRetrying(err)) {
          set({ user: null, status: 'unauthenticated' });
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 250 * 2 ** attempt));
      }
    }
  },
  async refreshUser() {
    try {
      const user = await api.me();
      set({ user, status: 'authenticated' });
    } catch {
      // Swallow — caller is responsible for surfacing failure. The
      // alternative would be flipping the user to unauthenticated mid-flow,
      // which surprises a freshly-rotated-password user with a logout.
    }
  },
}));
